import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Grade2EngineeringLesson.css";

// 3D dependencies:
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";

/** Shuffle array helper */
function shuffleArray(arr) {
  const cloned = [...arr];
  for (let i = cloned.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
}

/** Build question bank => total 50 => final quiz picks 50 */
function buildQuestionBank(poolA, poolB) {
  return [...poolA, ...poolB];
}

/**
 * 3D BuildingBlocks Demo, parallels your original "BuildingBlocks" but in 3D.
 *  We cycle among Bridge, Tower, House, each with a small 3D shape for demonstration.
 */
function BuildingBlocks3D() {
  const structures = [
    {
      name: "Bridge",
      description: "Bridges help people and cars cross over rivers!",
      example: "Golden Gate Bridge",
      color: "#ff7675",
      shape: "arch",
    },
    {
      name: "Tower",
      description: "Tall towers stand strong even in the wind!",
      example: "Eiffel Tower",
      color: "#74b9ff",
      shape: "tower",
    },
    {
      name: "House",
      description: "Houses give us shelter and keep us safe!",
      example: "A cozy home!",
      color: "#55efc4",
      shape: "house",
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const current = structures[currentIndex];

  const showNext = () => {
    setCurrentIndex((prev) => (prev + 1) % structures.length);
  };

  return (
    <div className="g2-buildings-3d-container">
      <h4>Explore a Structure (3D): {current.name}</h4>
      <p className="structure-description">{current.description}</p>
      <p className="structure-example">
        <strong>Example:</strong> {current.example}
      </p>
      <button className="next-structure-btn" onClick={showNext}>
        See Another Structure
      </button>

      <div style={{ width: "300px", height: "200px", background: "#f0f0f0", marginTop: "10px" }}>
        <Canvas camera={{ position: [0, 3, 5], fov: 50 }}>
          <OrbitControls enablePan={false} />
          <ambientLight intensity={0.5} />
          <directionalLight position={[5, 5, 5]} />
          {/* A ground plane */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1, 0]}>
            <planeGeometry args={[10, 10]} />
            <meshStandardMaterial color="#ccc" />
          </mesh>

          {/* Display the structure shape */}
          <StructureShape shape={current.shape} color={current.color} />
        </Canvas>
      </div>
    </div>
  );
}

/**
 * Basic shapes for each structure:
 *  - "arch" for the bridge,
 *  - "tower" for the tower,
 *  - "house" for a simple triangular roof + box.
 */
function StructureShape({ shape, color }) {
  if (shape === "arch") {
    // Represent a simple arch for "Bridge"
    return (
      <mesh position={[0, 0, 0]}>
        <torusGeometry args={[1, 0.3, 16, 32, Math.PI]} />
        <meshStandardMaterial color={color} />
      </mesh>
    );
  } else if (shape === "tower") {
    // A tall cylinder or box stack
    return (
      <mesh position={[0, 0, 0]}>
        <cylinderGeometry args={[0.5, 0.5, 2, 16]} />
        <meshStandardMaterial color={color} />
      </mesh>
    );
  } else if (shape === "house") {
    // A box + a triangular roof
    return (
      <group>
        {/* House box */}
        <mesh position={[0, 0.25, 0]}>
          <boxGeometry args={[1, 0.5, 1]} />
          <meshStandardMaterial color={color} />
        </mesh>
        {/* Roof: a simple triangular prism on top */}
        <mesh position={[0, 0.75, 0]} rotation={[0, 0, 0]}>
          <coneGeometry args={[0.7, 0.4, 4]} />
          <meshStandardMaterial color="#d63031" />
        </mesh>
      </group>
    );
  }
  return null;
}

/**
 * MAIN Grade2EngineeringLesson
 *  - Similar to Grade1EngineeringLesson structure
 *  - Has quick quiz + final quiz logic
 */
export default function Grade2EngineeringLesson() {
  // Topics
  const [selectedTopic, setSelectedTopic] = useState(null);

  // Quick Quiz States
  const [quiz, setQuiz] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);

  // Final Quiz States
  const [fullQuizQuestions, setFullQuizQuestions] = useState([]);
  const [fullQuizActive, setFullQuizActive] = useState(false);
  const [fullQuizIndex, setFullQuizIndex] = useState(0);
  const [fullQuizScore, setFullQuizScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(300); // 5 min timer

  // Timer effect for final quiz
  useEffect(() => {
    if (fullQuizActive && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFullQuizActive(false);
    }
  }, [fullQuizActive, timeLeft]);

  // Lesson Content
  const lessonContent = {
    howThingsAreBuilt: {
      title: "How Things Are Built",
      description: "Engineers design and build everything we use!",
      example: "Example: Buildings, cars, and even spaceships!",
      steps: [
        "Engineers use strong materials like wood, metal, and concrete.",
        "They plan carefully before building.",
        "They test their designs to make sure they are safe!",
      ],
    },
    strongStructures: {
      title: "Strong Structures",
      description: "Some shapes are stronger than others in buildings!",
      example: "Example: A triangle is one of the strongest shapes!",
      steps: [
        "Triangles help make bridges and towers strong.",
        "Domes and arches spread weight evenly.",
        "Rectangles make walls and floors stable!",
        // Replacing your old <BuildingBlocks> with 3D version:
        <BuildingBlocks3D key="building-blocks-3d" />,
      ],
    },
  };

  /**
   * 2) QUIZ POOLS: 25 each => total 50
   * "builtPool" for howThingsAreBuilt + "structuresPool" for strongStructures
   */
  const builtPool = [
    {
      question: "What do engineers use to build things?",
      correctAnswer: "Strong materials like wood & metal",
      options: [
        "Only paper and tape",
        "Paintbrushes only",
        "Strong materials like wood & metal",
      ],
    },
    {
      question: "Which of these do engineers NOT do before building?",
      correctAnswer: "Build without thinking",
      options: [
        "Create a plan",
        "Build without thinking",
        "Check if design is safe",
      ],
    },
    {
      question: "How do engineers ensure safety?",
      correctAnswer: "They test their designs",
      options: [
        "They guess randomly",
        "They test their designs",
        "They ignore safety",
      ],
    },
    {
      question: "Which is an example of something engineers build?",
      correctAnswer: "Spaceships",
      options: ["Spaceships", "Ice cream cones only", "Sandcastles only"],
    },
    {
      question: "Why do engineers plan carefully?",
      correctAnswer: "To avoid mistakes and keep structures safe",
      options: [
        "They like wasting time",
        "To avoid mistakes and keep structures safe",
        "They never plan, just build",
      ],
    },
    {
      question: "What might an engineer use to hold parts together?",
      correctAnswer: "Bolts and screws",
      options: ["Pillows", "Bolts and screws", "Crayons"],
    },
    {
      question: "Which material might an engineer NOT use?",
      correctAnswer: "Cotton candy",
      options: ["Steel beams", "Cotton candy", "Concrete blocks"],
    },
    {
      question: "When building a house, engineers check?",
      correctAnswer: "The foundation is strong",
      options: [
        "The foundation is strong",
        "If it’s a pretty color only",
        "If it can dance",
      ],
    },
    {
      question: "Before finalizing a design, engineers often?",
      correctAnswer: "Make prototypes or models",
      options: [
        "Ignore feedback",
        "Make prototypes or models",
        "Start building without a plan",
      ],
    },
    {
      question: "What keeps a building from falling?",
      correctAnswer: "A strong structure and foundation",
      options: [
        "Magic spells",
        "Only the paint color",
        "A strong structure and foundation",
      ],
    },
    {
      question: "Engineers building a bridge must consider?",
      correctAnswer: "The load it can carry",
      options: [
        "Only how fast cars go",
        "The load it can carry",
        "Where to place fireworks",
      ],
    },
    {
      question: "Which is true about how things are built?",
      correctAnswer: "They often follow detailed blueprints",
      options: [
        "They only follow random guesses",
        "They never use math",
        "They often follow detailed blueprints",
      ],
    },
    {
      question: "When building a car, an engineer might test?",
      correctAnswer: "Safety features and crash tests",
      options: [
        "Only the horn’s loudness",
        "Nothing at all",
        "Safety features and crash tests",
      ],
    },
    {
      question: "Engineers build using different tools like?",
      correctAnswer: "Wrenches, saws, and measuring tapes",
      options: [
        "Markers and forks only",
        "Wrenches, saws, and measuring tapes",
        "Spoons and cups",
      ],
    },
    {
      question: "Planning includes checking?",
      correctAnswer: "Costs, safety, and materials",
      options: [
        "Only the color scheme",
        "Costs, safety, and materials",
        "How many flags to add",
      ],
    },
    {
      question: "Which shape might engineers use for a building’s frame?",
      correctAnswer: "Rectangles or squares",
      options: [
        "Circles only",
        "Rectangles or squares",
        "No shape at all",
      ],
    },
    {
      question: "‘Measure twice, cut once’ means?",
      correctAnswer: "Double-check before building",
      options: [
        "Cut everything randomly",
        "Double-check before building",
        "Measure after cutting",
      ],
    },
    {
      question: "How do engineers learn from mistakes?",
      correctAnswer: "They redesign and improve",
      options: [
        "They give up and stop",
        "They sabotage the project",
        "They redesign and improve",
      ],
    },
    {
      question: "Engineers might create small-scale versions called?",
      correctAnswer: "Models",
      options: ["Songs", "Recipes", "Models"],
    },
    {
      question: "Which is NOT a step in engineering?",
      correctAnswer: "Singing the blueprint tune",
      options: [
        "Singing the blueprint tune",
        "Planning the design",
        "Testing the design",
      ],
    },
    {
      question: "Why do engineers want strong materials?",
      correctAnswer: "So the structure doesn’t break easily",
      options: [
        "Just for color variety",
        "So the structure doesn’t break easily",
        "No reason at all",
      ],
    },
    {
      question: "Engineers might design special buildings like?",
      correctAnswer: "Skyscrapers",
      options: [
        "Underground dungeons",
        "Skyscrapers",
        "Only small huts",
      ],
    },
    {
      question: "A ‘blueprint’ is?",
      correctAnswer: "A detailed plan showing how to build",
      options: [
        "A random doodle",
        "A color chart for paint",
        "A detailed plan showing how to build",
      ],
    },
    {
      question: "Which is true about how things are built?",
      correctAnswer: "It takes planning, tools, and testing",
      options: [
        "Random guesses always work",
        "No tools needed",
        "It takes planning, tools, and testing",
      ],
    },
    {
      question: "Engineers building spaceships must consider?",
      correctAnswer: "Weight, shape, and safety in space",
      options: [
        "Nothing special",
        "Weight, shape, and safety in space",
        "Just the paint color",
      ],
    },
  ];

  const structuresPool = [
    {
      question: "Which shape is known to be very strong in buildings?",
      correctAnswer: "Triangle",
      options: ["Circle", "Triangle", "Square only"],
    },
    {
      question: "Domes and arches are used because?",
      correctAnswer: "They spread weight evenly",
      options: [
        "They are easier to paint",
        "They spread weight evenly",
        "They look funny",
      ],
    },
    {
      question: "Rectangles are common in walls because?",
      correctAnswer: "They make floors and walls stable",
      options: [
        "They’re hard to build",
        "They always fall over",
        "They make floors and walls stable",
      ],
    },
    {
      question: "Bridges often use which shape for strength?",
      correctAnswer: "Triangles (trusses)",
      options: [
        "Circles only",
        "Triangles (trusses)",
        "Rectangles with no support",
      ],
    },
    {
      question: "A tower must stand in wind. Which shape might help?",
      correctAnswer: "Triangles or cross-braces",
      options: [
        "Only circles stacked",
        "Hollow cubes only",
        "Triangles or cross-braces",
      ],
    },
    {
      question: "Houses often have which shape for the roof?",
      correctAnswer: "A triangular or slanted shape",
      options: [
        "A perfect sphere",
        "A triangular or slanted shape",
        "A flat circle",
      ],
    },
    {
      question: "An arch can support weight because?",
      correctAnswer: "It redirects force down its curve",
      options: [
        "It’s just fancy decoration",
        "It redirects force down its curve",
        "It’s always made of foam",
      ],
    },
    {
      question: "Which is NOT a strong structural shape?",
      correctAnswer: "A random wiggly line",
      options: [
        "Triangles",
        "Domes",
        "A random wiggly line",
      ],
    },
    {
      question: "Engineers test structures by?",
      correctAnswer: "Applying weight or forces",
      options: [
        "Painting them pink",
        "Applying weight or forces",
        "Singing to them",
      ],
    },
    {
      question: "A floor beam might be which shape?",
      correctAnswer: "A long rectangle or I-beam",
      options: [
        "A spherical balloon",
        "A long rectangle or I-beam",
        "A circle ring only",
      ],
    },
    {
      question: "In a tall tower, cross-bracing is used with?",
      correctAnswer: "Diagonal bars forming triangles",
      options: [
        "Horizontal lines only",
        "Diagonal bars forming triangles",
        "No bars at all",
      ],
    },
    {
      question: "A dome on a stadium is strong because?",
      correctAnswer: "It’s curved and distributes weight",
      options: [
        "It has no structure inside",
        "It’s made of confetti",
        "It’s curved and distributes weight",
      ],
    },
    {
      question: "Which best describes a ‘truss’?",
      correctAnswer: "A framework of triangles",
      options: [
        "A rectangular frame only",
        "A single circle",
        "A framework of triangles",
      ],
    },
    {
      question: "Arches are seen in old bridges because?",
      correctAnswer: "They can support heavy loads with fewer materials",
      options: [
        "They are purely decorative",
        "They can support heavy loads with fewer materials",
        "They only work in small projects",
      ],
    },
    {
      question: "Walls often use studs placed?",
      correctAnswer: "Vertically and spaced evenly",
      options: [
        "Randomly scattered",
        "Vertically and spaced evenly",
        "In no pattern at all",
      ],
    },
    {
      question: "Why is a triangle stable?",
      correctAnswer: "Its corners brace each other so it can’t collapse easily",
      options: [
        "It collapses easily",
        "It’s only used for decoration",
        "Its corners brace each other so it can’t collapse easily",
      ],
    },
    {
      question: "If you see a big tower with cross lines, that’s?",
      correctAnswer: "Triangular bracing for strength",
      options: [
        "Purely a ladder design",
        "Rectangles stacked",
        "Triangular bracing for strength",
      ],
    },
    {
      question: "A house with a rectangular frame is stable because?",
      correctAnswer: "All angles are 90° and it’s well-supported",
      options: [
        "Rectangles are soft shapes",
        "All angles are 90° and it’s well-supported",
        "It’s only aesthetic",
      ],
    },
    {
      question: "Engineers choose shapes like triangles or arches because?",
      correctAnswer: "They distribute force to stay strong",
      options: [
        "They look fancier only",
        "They distribute force to stay strong",
        "They’re easy to draw",
      ],
    },
    {
      question: "A roof might be triangular to?",
      correctAnswer: "Allow rain or snow to slide off easily",
      options: [
        "Decorate with flags",
        "Allow rain or snow to slide off easily",
        "Because squares are illegal",
      ],
    },
    {
      question: "Which shape is often used for support in bridges & towers?",
      correctAnswer: "Triangles",
      options: ["Cylinders", "Triangles", "2D circles only"],
    },
    {
      question: "Arches in a building help by?",
      correctAnswer: "Spreading the load across the curve",
      options: [
        "Turning gravity off",
        "Spreading the load across the curve",
        "Blocking the doorway",
      ],
    },
    {
      question: "A rectangle can become stronger by?",
      correctAnswer: "Adding diagonal braces (making triangles inside)",
      options: [
        "Removing corners",
        "Adding diagonal braces (making triangles inside)",
        "Filling it with candy",
      ],
    },
    {
      question: "Which is true about strong structures?",
      correctAnswer: "They often use triangles or arches",
      options: [
        "They ignore geometry",
        "They often use triangles or arches",
        "They can’t hold any weight",
      ],
    },
    {
      question: "Domes on sports stadiums are shaped to?",
      correctAnswer: "Distribute weight and stand large spans",
      options: [
        "Collect water on top",
        "Distribute weight and stand large spans",
        "Wobble in the wind",
      ],
    },
  ];

  // ** Quick Quiz Logic **
  const generateQuiz = (topic) => {
    let questionItem;
    if (topic === "howThingsAreBuilt") {
      // pick from builtPool
      questionItem = builtPool[Math.floor(Math.random() * builtPool.length)];
    } else {
      // pick from structuresPool
      questionItem = structuresPool[Math.floor(Math.random() * structuresPool.length)];
    }
    const newQuiz = {
      question: questionItem.question,
      correctAnswer: questionItem.correctAnswer,
      options: shuffleArray([...questionItem.options]),
    };
    setQuiz(newQuiz);
    setSelectedAnswer(null);
    setFeedback(null);
  };

  const handleAnswerClick = (answer) => {
    setSelectedAnswer(answer);
    if (quiz && answer === quiz.correctAnswer) {
      setFeedback("✅ Correct!");
    } else {
      setFeedback("❌ Try Again!");
    }
    // Optionally re-generate a new question after some delay
    setTimeout(() => {
      if (selectedTopic) generateQuiz(selectedTopic);
    }, 1500);
  };

  // ** Final Quiz Logic => pick 50 from the 50 total (25 each) **
  const startFinalQuiz = () => {
    const bigPool = buildQuestionBank(builtPool, structuresPool);
    const finalSet = shuffleArray(bigPool).slice(0, 50); // We have exactly 50, but slicing to be safe
    setFullQuizQuestions(finalSet);
    setFullQuizActive(true);
    setFullQuizIndex(0);
    setFullQuizScore(0);
    setTimeLeft(300);
  };

  const handleFinalQuizAnswer = (choice) => {
    if (
      fullQuizQuestions[fullQuizIndex] &&
      choice === fullQuizQuestions[fullQuizIndex].correctAnswer
    ) {
      setFullQuizScore((prev) => prev + 1);
    }
    if (fullQuizIndex < fullQuizQuestions.length - 1) {
      setFullQuizIndex((prev) => prev + 1);
    } else {
      setFullQuizActive(false);
    }
  };

  return (
    <div className="grade2-engineering-page">
      <h1 className="grade2-engineering-title">Grade 2 Engineering Lessons</h1>
      <p className="grade2-engineering-description">Click a topic to learn more:</p>

      <div className="grade2-engineering-topics">
        <button
          className="grade2-engineering-card"
          onClick={() => {
            setSelectedTopic("howThingsAreBuilt");
            generateQuiz("howThingsAreBuilt");
          }}
        >
          How Things Are Built
        </button>
        <button
          className="grade2-engineering-card"
          onClick={() => {
            setSelectedTopic("strongStructures");
            generateQuiz("strongStructures");
          }}
        >
          Strong Structures
        </button>
      </div>

      {/* Lesson + Quiz Container */}
      {selectedTopic && (
        <div className="g2-lesson-quiz-container">
          {/* Lesson Content (left) */}
          <div className="grade2-engineering-content">
            <h2>{lessonContent[selectedTopic].title}</h2>
            <p>{lessonContent[selectedTopic].description}</p>
            <p><strong>{lessonContent[selectedTopic].example}</strong></p>
            <ul>
              {lessonContent[selectedTopic].steps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ul>
          </div>

          {/* Quick Quiz (right) */}
          <div className="g2-quiz-container">
            <h2>Quick Quiz!</h2>
            {quiz && (
              <>
                <p>{quiz.question}</p>
                <div className="g2-quiz-options">
                  {quiz.options.map((option, index) => (
                    <button
                      key={index}
                      className={selectedAnswer === option ? "selected" : ""}
                      onClick={() => handleAnswerClick(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                {feedback && <p>{feedback}</p>}
              </>
            )}
          </div>
        </div>
      )}

      {/* Final Quiz Section */}
      <div className="g2-final-quiz-section">
        {!fullQuizActive && (
          <button className="g2-final-quiz-btn" onClick={startFinalQuiz}>
            Start Final Quiz (50 Questions)
          </button>
        )}
        {fullQuizActive && (
          <div className="g2-final-quiz-container">
            <h2>Final Quiz (50 Questions)</h2>
            <p>
              Time Left: {Math.floor(timeLeft / 60)}:
              {String(timeLeft % 60).padStart(2, "0")}
            </p>
            <p>
              {fullQuizIndex + 1}/50: {fullQuizQuestions[fullQuizIndex].question}
            </p>
            <div className="g2-quiz-options">
              {fullQuizQuestions[fullQuizIndex].options.map((choice, idx) => (
                <button key={idx} onClick={() => handleFinalQuizAnswer(choice)}>
                  {choice}
                </button>
              ))}
            </div>
            {fullQuizIndex === 49 && <p>Final Score: {fullQuizScore}/50</p>}
          </div>
        )}
      </div>

      <Link to="/engineering" className="grade2-engineering-back">
        ← Back to Engineering Page
      </Link>
    </div>
  );
}
